import { IData } from './iData.js';
import { IClientData } from './iClientData.js';
import { IClient } from './iClient.js';
import { IView } from './iView.js';
import { IAction } from './iAction.js';
import { IAppSite } from './iAppSite.js';
import { IDataReloadable } from './iDataReloadable.js';
import { IDataSource } from './iDataSource.js';

/**
 * Interface for registry implementations that register view, data, and action providers
 */
export abstract class IRegistry {
    /**
     * Registers view providers to the dynamic view list
     */
    abstract registerView(dynamicViewList: Map<string, IView>): void;

    /**
     * Registers data providers to the dynamic data list
     */
    abstract registerData(dynamicDataList: Map<string, IData>): void;

    /**
     * Registers CLIENT-DATA preparer providers to the dynamic client-data list. Concrete no-op default
     * (mirrors the C# IRegistry default body) so modules that ship no client-data preparers compile
     * unchanged. Modules that own client-data preparers override this.
     */
    registerClientData(dynamicClientDataList: Map<string, IClientData>): void {
        void dynamicClientDataList;
    }

    /**
     * Registers CLIENT HANDLER processors to the client-handler list. Concrete no-op default (mirrors the
     * C# IRegistry default body) so modules that ship no client handlers compile unchanged. Modules that own
     * client handlers override this.
     */
    registerClient(clientHandlerList: Map<string, IClient>): void {
        void clientHandlerList;
    }

    /**
     * Registers action providers to the dynamic action list
     */
    abstract registerAction(dynamicActionList: Map<string, IAction>): void;

    /**
     * Registers the AppSite handlers exposed by the module. Concrete no-op default (mirrors the
     * C# IRegistry default body) so existing modules that do not provide AppSite handlers compile
     * unchanged. Modules that own AppSite handlers override this.
     */
    registerAppSite(dynamicAppSiteList: Map<string, IAppSite>): void {
        void dynamicAppSiteList;
    }

    /**
     * Registers app-data backed runtime services that can be reloaded after persisted changes.
     */
    abstract registerReloadable(appDataReloadables: Map<string, IDataReloadable[]>): void;

    /**
     * Registers capture-source resolvers (keyed by scheme) used by the generic ordered-scenario
     * capture/substitution mechanism. Most modules register none.
     */
    abstract registerDataSource(dataSources: Map<string, IDataSource>): void;
}
